import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Skeleton } from '../components/ui/skeleton';
import { base64ToArrayBuffer, unwrapKeyFromSender } from '../lib/crypto';
import { decryptPost, getOrCreateFeedKey } from '../lib/feed-key';
import { t } from '../lib/i18n';
import { api } from '../lib/trpc/client';

type ProfilePost = {
  postId: string;
  timestamp: string;
  keyVersion: string;
  content: string;
};

export function ProfilePage() {
  const { username } = useParams<{ username: string }>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [profile, setProfile] = useState<{ userId: string; scratchUsername: string; createdAt: string } | null>(null);
  const [fingerprint, setFingerprint] = useState<string>('');
  const [posts, setPosts] = useState<ProfilePost[]>([]);

  useEffect(() => {
    const run = async () => {
      if (!username) {
        setError(t('ユーザー名が必要です', 'Username is required'));
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const activeCircle = await api.circle.getActiveCircle();
        if (!activeCircle) {
          throw new Error(t('アクティブサークルがありません', 'No active circle selected'));
        }

        const [targetProfile, identity] = await Promise.all([
          api.user.getProfileByUsername({ username }),
          api.user.getIdentityPk({ username })
        ]);
        setProfile(targetProfile);
        setFingerprint(identity.fingerprint);

        const postResponse = await api.feed.getPostsByUser({ userId: targetProfile.userId, limit: 30 });
        const ownerId = activeCircle.ownerUserId;
        const currentUserId = localStorage.getItem('userId');

        const resolveKey = async (version: string): Promise<CryptoKey> => {
          if (currentUserId === ownerId) {
            return getOrCreateFeedKey(version);
          }

          if (!currentUserId) {
            throw new Error(t('セッションが見つかりません', 'No active session'));
          }

          const identitySkRaw = localStorage.getItem('identitySkRaw');
          if (!identitySkRaw) {
            throw new Error(t('ローカル秘密鍵がありません', 'Local identity key missing'));
          }

          const keyResult = await api.feed.getFeedKeys({ userId: currentUserId });
          const encrypted = keyResult.encryptedKeys.find((item) => item.version === version && item.ephemeralPk);
          if (!encrypted || !encrypted.ephemeralPk) {
            throw new Error(t('共有鍵が見つかりません', 'Shared key not found'));
          }

          return unwrapKeyFromSender(
            base64ToArrayBuffer(encrypted.ciphertext),
            base64ToArrayBuffer(encrypted.ephemeralPk),
            base64ToArrayBuffer(identitySkRaw)
          );
        };

        const decrypted = await Promise.all(
          postResponse.posts.map(async (post): Promise<ProfilePost> => {
            try {
              return {
                postId: post.postId,
                timestamp: post.timestamp,
                keyVersion: post.keyVersion,
                content: await decryptPost(post.ciphertext, await resolveKey(post.keyVersion))
              };
            } catch {
              return {
                postId: post.postId,
                timestamp: post.timestamp,
                keyVersion: post.keyVersion,
                content: t('[復号失敗]', '[decrypt failed]')
              };
            }
          })
        );

        setPosts(decrypted);
      } catch (err) {
        setError(err instanceof Error ? err.message : t('プロフィールの取得に失敗しました', 'Failed to load profile'));
      } finally {
        setLoading(false);
      }
    };

    void run();
  }, [username]);

  if (loading) {
    return (
      <main className="mx-auto min-h-screen w-full max-w-2xl space-y-3 px-4 py-10">
        <Skeleton className="h-28 w-full" />
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-24 w-full" />
      </main>
    );
  }

  return (
    <main className="mx-auto min-h-screen w-full max-w-2xl space-y-4 px-4 py-10">
      {error ? (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle>{profile?.scratchUsername ?? username}</CardTitle>
          <CardDescription>{t('フィンガープリント', 'Fingerprint')}: {fingerprint || '-'}</CardDescription>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground">
          {t('登録日', 'Joined')}: {profile ? new Date(profile.createdAt).toLocaleString() : '-'}
        </CardContent>
      </Card>

      {posts.length === 0 ? (
        <Card>
          <CardContent className="py-10 text-center text-sm text-muted-foreground">{t('投稿がありません。', 'No posts.')}</CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {posts.map((post) => (
            <Card key={post.postId}>
              <CardHeader className="space-y-1 pb-2">
                <CardTitle className="text-base">{new Date(post.timestamp).toLocaleString()}</CardTitle>
                <CardDescription>{t('鍵', 'Key')}: {post.keyVersion}</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="whitespace-pre-wrap text-sm">{post.content}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </main>
  );
}
